// packages import
import { FC } from 'react'
import { Box, Chip, Stack, TableBody, TableCell, TableHead, TableRow, Tooltip, Typography } from '@mui/material'

// others
import { ExternalUserRoleRole } from '../../../generated'
import {
  ADVERSARY_TABLE_HEADERS,
  CLIENT_TABLE_HEADERS,
  CONTACT_TABLE_HEADERS,
  JUDGE_TABLE_HEADERS,
  NOT_APPLICABLE
} from '../../constants'
import { ExternalUsersListingProps } from '../../interfaceTypes'
import { ellipsisTwoLine, flexCenter } from '../../theme/styleConstants'
import { capitalizeCase, formatPhoneHyphenated, removeUnderScoreOrHyphenAndToLowerCase, titleize } from '../../utils'

// component import
import { CustomPagination } from '../common/CustomPagination'
import { EmptyData } from '../common/EmptyData'
import { StickyTable } from '../common/StickyTable'
import { ExternalUsersMenu } from '../menus/ExternalUsers'
import { EditAdversary } from './EditAdversary'
import { EditClient } from './EditClient'
import { EditJudge } from './EditJudge'
import { RemoveExternalUser } from './RemoveExternalUser'
import { TableSkeleton } from '../skeletons/TableSkeleton'
import { Link } from 'react-router-dom'

export const ExternalUsersListing: FC<ExternalUsersListingProps> = ({
  state,
  dispatch: externalUserDispatch,
  loading,
  refetch,
  emptyMessage,
  roleType
}) => {
  const { allExternalUsers } = state || {}
  const isEmpty = allExternalUsers?.length === 0 && !loading
  const { currentExternalUser } = state || {}

  const isClient = roleType === ExternalUserRoleRole.Client
  const isAdversary = roleType === ExternalUserRoleRole.Adversary
  const isJudge = roleType === ExternalUserRoleRole.Judge
  const isContact = roleType === ExternalUserRoleRole.Contact

  return (
    <>
      {isEmpty ? (
        <Box sx={{ ...flexCenter, height: 'calc(100vh - 400px)' }}>
          <EmptyData title={emptyMessage || ''} />
        </Box>
      ) : (
        <Box sx={isClient ? { width: 0, minWidth: '100%', overflowX: 'auto' } : undefined}>
          <StickyTable pagination={<CustomPagination loading={loading || false} />} height="381px">
            <TableHead>
              <TableRow hover>
                {isClient && <TableCell sx={{ minWidth: '200px ' }}> Client # </TableCell>}

                {isClient &&
                  CLIENT_TABLE_HEADERS.map((item, index) => {
                    const lastIndex = CLIENT_TABLE_HEADERS.length - 1

                    return (
                      <TableCell
                        key={index}
                        component="th"
                        align={index === lastIndex ? 'center' : 'left'}
                        sx={{ minWidth: index === lastIndex ? '50px' : '200px ' }}
                      >
                        {item}
                      </TableCell>
                    )
                  })}

                {isAdversary &&
                  ADVERSARY_TABLE_HEADERS.map((item, index) => {
                    const lastIndex = ADVERSARY_TABLE_HEADERS.length - 1

                    return (
                      <TableCell
                        key={index}
                        component="th"
                        align={index === lastIndex ? 'center' : 'left'}
                        sx={{ minWidth: index === lastIndex ? '50px' : '200px ' }}
                      >
                        {item}
                      </TableCell>
                    )
                  })}

                {isJudge &&
                  JUDGE_TABLE_HEADERS.map((item, index) => {
                    const lastIndex = JUDGE_TABLE_HEADERS.length - 1

                    return (
                      <TableCell
                        key={index}
                        component="th"
                        align={index === lastIndex ? 'center' : 'left'}
                        sx={{ minWidth: index === lastIndex ? '50px' : '200px ' }}
                      >
                        {item}
                      </TableCell>
                    )
                  })}
                {isContact &&
                  CONTACT_TABLE_HEADERS.map((item, index) => {
                    const lastIndex = CONTACT_TABLE_HEADERS.length - 1

                    return (
                      <TableCell key={index} component="th" sx={{ minWidth: index === lastIndex ? '50px' : '200px ' }}>
                        {item}
                      </TableCell>
                    )
                  })}
              </TableRow>
            </TableHead>

            {loading ? (
              <TableSkeleton
                noOfRows={6}
                noOfCols={
                  isClient
                    ? CLIENT_TABLE_HEADERS.length + 1
                    : isAdversary || isContact
                    ? ADVERSARY_TABLE_HEADERS.length
                    : JUDGE_TABLE_HEADERS.length
                }
              />
            ) : (
              <TableBody>
                {allExternalUsers?.map(externalUser => {
                  const {
                    id,
                    firstName,
                    middleName,
                    lastName,
                    businessName,
                    displayName,
                    initials,
                    email,
                    addresses,
                    phoneNumber,
                    homePhone,
                    personalPhone,
                    clientNumber,
                    phoneNumberExtension,
                    contractBalance,
                    courtToExternalUserToExternalUserRole,
                    externalUserRoles
                  } = externalUser || {}
                  const courts = courtToExternalUserToExternalUserRole?.map(item => item?.court)
                  const fullAddress =
                    addresses?.length &&
                    `${addresses[0]!.address1 || ''} ${addresses[0]!.address2 || ''} ${addresses[0]!.city || ''} ${
                      addresses[0]!.state || ''
                    } ${addresses[0]!.country || ''} ${addresses[0]!.zipCode?.padStart(5, '0') || ''}`

                  const assignedRoles = externalUserRoles
                    ?.map(externalUserRole => {
                      return externalUserRole?.caseToExternalUserToExternalUserRole?.map(cte => ({
                        caseRole: cte?.caseRole,
                        caseTitle: cte?.litigation?.title,
                        caseNumber: cte?.litigation?.caseNumber ?? cte?.litigation?.consultMatterNumber
                      }))
                    })
                    .flatMap(role => role)

                  return (
                    <TableRow key={id} hover>
                      {isClient && (
                        <TableCell>
                          {' '}
                          <Tooltip
                            title={clientNumber ? `#${clientNumber}` : NOT_APPLICABLE}
                            arrow
                            placement="bottom-start"
                          >
                            <Link to={`${clientNumber}`}>
                              <Typography variant="body1" noWrap color="primary">
                                {clientNumber ? `#${clientNumber}` : NOT_APPLICABLE}
                              </Typography>
                            </Link>
                          </Tooltip>
                        </TableCell>
                      )}

                      {isClient ? (
                        <>
                          <TableCell sx={{ minWidth: '150px' }}>
                            <Typography variant="body1" noWrap>
                              {firstName || NOT_APPLICABLE}
                            </Typography>
                          </TableCell>
                          <TableCell sx={{ minWidth: '150px' }}>
                            <Typography variant="body1" noWrap>
                              {middleName || NOT_APPLICABLE}
                            </Typography>
                          </TableCell>
                          <TableCell sx={{ minWidth: '150px' }}>
                            <Typography variant="body1" noWrap>
                              {lastName || NOT_APPLICABLE}
                            </Typography>
                          </TableCell>
                          <TableCell sx={{ minWidth: '150px' }}>
                            <Tooltip title={businessName || NOT_APPLICABLE} arrow>
                              <Typography variant="body1" noWrap>
                                {businessName || NOT_APPLICABLE}
                              </Typography>
                            </Tooltip>
                          </TableCell>
                          <TableCell sx={{ minWidth: '180px' }}>
                            <Tooltip title={email || NOT_APPLICABLE} arrow>
                              <Typography noWrap color="wardGray" fontSize="14px">
                                {email || NOT_APPLICABLE}
                              </Typography>
                            </Tooltip>
                          </TableCell>
                        </>
                      ) : (
                        <TableCell>
                          <Box maxWidth="250px">
                            <Tooltip
                              title={`${displayName || '--'}${initials ? `, (${initials})` : ''}`}
                              arrow
                              placement="top-start"
                            >
                              <Link to={`${id}`}>
                                <Typography variant="body1" noWrap sx={{ minWidth: '120px' }} color="primary">
                                  {displayName || '--'}
                                </Typography>
                              </Link>
                            </Tooltip>

                            <Tooltip title={email} arrow>
                              <Typography noWrap color="wardGray" fontSize="14px">
                                {email || NOT_APPLICABLE}
                              </Typography>
                            </Tooltip>
                          </Box>
                        </TableCell>
                      )}

                      {isClient && (
                        <TableCell sx={{ minWidth: '120px' }}>{contractBalance || NOT_APPLICABLE}</TableCell>
                      )}

                      {!isJudge && (
                        <TableCell>
                          <Tooltip title={fullAddress || NOT_APPLICABLE} arrow>
                            <Typography sx={{ ...ellipsisTwoLine, maxWidth: '300px' }}>
                              {fullAddress || NOT_APPLICABLE}
                            </Typography>
                          </Tooltip>
                        </TableCell>
                      )}

                      {isJudge && (
                        <TableCell sx={{ minWidth: '120px' }}>{phoneNumberExtension || NOT_APPLICABLE} </TableCell>
                      )}

                      <TableCell sx={{ minWidth: '120px' }}>
                        {formatPhoneHyphenated(phoneNumber || homePhone || personalPhone || NOT_APPLICABLE)}{' '}
                      </TableCell>

                      {(isContact || isAdversary) && (
                        <TableCell>
                          {!assignedRoles?.length ? (
                            <Typography paddingLeft={1}>{NOT_APPLICABLE}</Typography>
                          ) : (
                            <>
                              <Stack direction="row" flexWrap="wrap" gap="10px">
                                {assignedRoles?.slice(0, 3).map(assignment => {
                                  const { caseNumber, caseTitle, caseRole } = assignment || {}
                                  const title = `(#${caseNumber}) ${titleize(caseTitle || '')} - ${titleize(
                                    removeUnderScoreOrHyphenAndToLowerCase(caseRole || '')
                                  )}`
                                  return (
                                    <Tooltip
                                      title={
                                        caseNumber ? `(#${caseNumber}) ${titleize(caseTitle || '')}` : NOT_APPLICABLE
                                      }
                                      arrow
                                    >
                                      <Chip key={`${caseNumber}-${caseTitle}`} label={title} variant="outlined" />
                                    </Tooltip>
                                  )
                                })}
                              </Stack>

                              {assignedRoles?.length! > 3 && (
                                <Tooltip
                                  title={assignedRoles?.slice(3)?.map(assignment => {
                                    const { caseNumber, caseRole, caseTitle } = assignment || {}
                                    const title = `(#${caseNumber}) ${titleize(caseTitle || '')} - ${titleize(
                                      removeUnderScoreOrHyphenAndToLowerCase(caseRole || '')
                                    )}`

                                    return (
                                      <Typography color="white" key={id}>
                                        {title}
                                      </Typography>
                                    )
                                  })}
                                  sx={{ gap: '10px' }}
                                  placement="bottom-start"
                                >
                                  <Box pt={1}>
                                    <Chip
                                      key={id}
                                      color="primary"
                                      label={`+${assignedRoles?.length && assignedRoles?.length - 3}`}
                                      variant="outlined"
                                    />
                                  </Box>
                                </Tooltip>
                              )}
                            </>
                          )}
                        </TableCell>
                      )}

                      {isJudge && (
                        <TableCell>
                          {courts?.length === 0 ? (
                            <Typography>{NOT_APPLICABLE}</Typography>
                          ) : (
                            <>
                              <Stack direction="row" flexWrap="wrap" gap="10px">
                                {courts?.slice(0, 3).map(assignedCourt => {
                                  const { name } = assignedCourt || {}

                                  return (
                                    <Chip
                                      key={`${id}-${firstName}`}
                                      label={capitalizeCase(name || '')}
                                      variant="outlined"
                                    />
                                  )
                                })}
                              </Stack>

                              {courts?.length! > 3 && (
                                <Tooltip
                                  title={courts?.slice(3)?.map(assignedCourt => {
                                    const { name } = assignedCourt || {}

                                    return (
                                      <Typography color="white" key={id}>
                                        {capitalizeCase(name || '')}
                                      </Typography>
                                    )
                                  })}
                                  sx={{ gap: '10px' }}
                                  placement="bottom-start"
                                >
                                  <Box pt={1}>
                                    <Chip
                                      key={id}
                                      color="primary"
                                      label={`+${courts?.length && courts?.length - 3}`}
                                      variant="outlined"
                                    />
                                  </Box>
                                </Tooltip>
                              )}
                            </>
                          )}
                        </TableCell>
                      )}

                      <TableCell align="center">
                        <ExternalUsersMenu
                          currentExternalUser={externalUser!}
                          externalUserDispatch={externalUserDispatch}
                        />
                      </TableCell>
                    </TableRow>
                  )
                })}
              </TableBody>
            )}
          </StickyTable>
        </Box>
      )}

      <RemoveExternalUser state={state} dispatch={externalUserDispatch} refetch={refetch} roleType={roleType} />

      {isClient && (
        <EditClient
          state={state}
          userDetails={currentExternalUser}
          refetch={refetch}
          dispatch={externalUserDispatch}
          editClientNumberOnly
        />
      )}

      {isContact && (
        <EditAdversary
          state={state}
          userDetails={currentExternalUser}
          refetch={refetch}
          dispatch={externalUserDispatch}
          editClientNumberOnly={false}
          modalTitle="Contact Details"
          successMessage="Contact updated!"
        />
      )}

      {isAdversary && (
        <EditAdversary
          state={state}
          userDetails={currentExternalUser}
          refetch={refetch}
          dispatch={externalUserDispatch}
          editClientNumberOnly={false}
        />
      )}

      {isJudge && <EditJudge state={state} dispatch={externalUserDispatch} />}
    </>
  )
}
